import { Component, OnInit, Output, EventEmitter, Input, OnChanges, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { Subscription } from 'rxjs';

import { Availability, Package, Venue } from 'src/app/models/venues.model';
import { PaymentService } from 'src/app/services/payment.service';
import { HttpService } from 'src/app/services/http.service';

@Component({
    selector: 'app-customer-details',
    templateUrl: './customer-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerDetailsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() loading = false;
    @Input() primaryColor: string;
    @Input() backgroundColor: string;
    @Input() accentColor: string;
    @Input() textColor: string;
    @Input() textColor2: string;
    @Input() session: Availability;
    @Input() venue: Venue;
    @Input() package: any;
    @Input() payProfiles: any;
    @Input() groupTypes: {[key:string]:string};
    @Input() sourceTypes: {[key:string]:string};
    @Input() formSettings: { field: string, value?: string, lock?: boolean }[] = [];
    @Input() inline: string;
    @Input() extras: Package;
    @Input() booking: any;
    @Input() company: any;
    @Input() brandId: any;

    @Output() updateField: EventEmitter<{ field: string, value: any }> = new EventEmitter();
    @Output() bookNow: EventEmitter<{ form: any, priceOptions: any[] }> = new EventEmitter();
    @Output() enquire: EventEmitter<{ form: any, priceOptions: any[] }> = new EventEmitter();

    public faChevronLeft = faChevronLeft;
    public faPlus = faPlus;
    public faMinus = faMinus;
    public faLock = faLock;
    public faInfoCircle = faInfoCircle;
    public form: UntypedFormGroup;
    public people = 0;
    public availMessage = 'blah';
    public availStatus = '';

    public chatbotFeature = false;

    public morePaxNeeded = 0;
    public tooManyExtras = 0;
    public hasExtras = false;
    
    public total: number = 0;
    public fee: number = 0;

    public get feeLabel(): string {
        return this.payment.feeLabel || 'booking fee';
    }

    private availabilities: any[] = [];
    private sessionsForExtras: any = {};
    //public extras: any[] = [];

    private formSub: Subscription;

    @ViewChild('groupDetailsEl') groupDetailsRef: ElementRef;
    @ViewChild('chatbotEl') chatbotRef: ElementRef;

    constructor(
        private builder: UntypedFormBuilder,
        private payment: PaymentService,
        private http: HttpService
    ) { }

    ngOnInit() {
        //change all the options
        //console.log('init', this.morePaxNeeded, this.people, this.booking);

        if(this.booking){
            this.people = this.booking.qty_requested;
        }
        /*this.session.price_options.forEach(opt=>{
                this.changeOption(opt,0);
                console.log('init', this.morePaxNeeded, this.people);
            }
        );*/
        //console.log('init', this.morePaxNeeded, this.people);

        this.payment.init(this.venue?.pay_profile, this.payProfiles);
        if(this?.booking?.booking_id === undefined){
            this.availStatus = this.session.avail_status;
            if(this.session.avail_status == 'limited'){
                    if(this.venue.company_id == '2')
                        this.availMessage = 'Limited Availability';
                    else
                        this.availMessage = 'Limited Availability. Please call before booking';
            }else{
                    this.availMessage = '';
            }
        }else{
            this.availMessage = 'Booking Exists';
        }

        let first_name = this.booking?.customer_name&&this.booking.customer_name.split(' ')[0];
        let last_name = this.booking?.customer_name&&this.booking.customer_name.split(' ').splice(1,999);

        this.form = this.builder.group({
            first_name: [first_name??''],
            last_name: [last_name??''],
            customer_email: [this.booking?.customer_email??''],
            customer_tel: [this.booking?.customer_tel??''],
            title: [this.booking?.title??''],
            group_type: [null],
            source_type: [null],
            preferred_time: [null],
            message: [''],
            subscribed: [false]
        });

        this.preloadPreferredTime();

        this.calculateExtras();
        this.calculateTotal();

        setTimeout(()=>this.groupDetailsRef.nativeElement.scrollIntoView(),200);

        if(this.company.company_id == '2'){
            this.chatbotFeature = true;
        }
        setTimeout(()=>{
            if(this.chatbotFeature){
                if(this.chatbotRef && this.chatbotRef?.nativeElement){
                    this.chatbotRef.nativeElement.addEventListener('askQuestion',(a)=>this.askQuestion(a));
                }
            }
        },1000)

        if(this.package.price_options.filter(it=>+it.extra).length > 0){
            this.hasExtras = true;
        }else{
            this.hasExtras = false;
        }

        this.http.view_params.total = this.total;
        this.http.view_params.currency = this.company?.currency;
        this.http.view_params.product_ids = [`v${this.venue?.venue_id}va${this.package?.activity_id}ap${this.package?.package_id}p`];
        console.log('before widgetView');
        setTimeout(()=>this.http.logWidgetView('customer-details'), 1000);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.form && (changes.booking || (
            changes.session &&
            (String(changes.session.previousValue?.session_id) !== String(this.session?.session_id) ||
                !this.hasPreferredTime)
        ))) this.preloadPreferredTime();
        if (changes.formSettings && changes.formSettings.currentValue) this.setForm();
    }

    private get savedPreferredTime(): string | null {
        const savedPreference = this.booking?.components?.find(item =>
            String(item.session_id) === String(this.session?.session_id) &&
            String(item.package_id) === String(this.package?.package_id) &&
            String(item.venue_id) === String(this.venue?.venue_id)
        )?.preferred_time;
        const savedValue = typeof savedPreference === 'string' ? savedPreference.trim() : '';
        if (!savedValue || savedValue === '0') return null;
        return /^\d{2}:\d{2}:\d{2}$/.test(savedValue)
            ? savedValue.slice(0, 5) : savedValue;
    }

    private preloadPreferredTime(): void {
        const preferredTime = this.savedPreferredTime;
        this.form.get('preferred_time').setValue(
            this.preferredTimeOptions.find(option =>
                option.value === preferredTime ||
                option.label.toLowerCase() === preferredTime?.toLowerCase()
            )?.value ?? null
        );
    }

    public get preferredTimeOptions(): { label: string, value: string }[] {
        const parts = (this.session?.session_name || this.session?.name || '').split(/\s+or\s+/i);

        const options: { label: string, value: string }[] = [];
        for (const part of parts.length === 2 && parts.every(part => part.trim()) ? parts : []) {
            const label = part.trim();
            const timeLabel = /^(?:12\s*)?noon$/i.test(label) ? '12pm' :
                /^(?:12\s*)?midnight$/i.test(label) ? '12am' : label;
            const match = timeLabel.match(/^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/i);
            let value = label;
            if (match) {
                let hours = Number(match[1]);
                const minutes = Number(match[2] || 0);
                const meridiem = match[3]?.toLowerCase();
                if (minutes <= 59 && (meridiem ? hours >= 1 && hours <= 12 : hours <= 23)) {
                    if (meridiem) hours = hours % 12 + (meridiem === 'pm' ? 12 : 0);
                    value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                }
            }
            options.push({ label, value });
        }
        const savedPreference = this.savedPreferredTime;
        if (savedPreference && !options.some(option =>
            option.value === savedPreference ||
            option.label.toLowerCase() === savedPreference.toLowerCase()
        )) {
            options.push({ label: savedPreference, value: savedPreference });
        }
        return options;
    }

    public get hasPreferredTime(): boolean {
        return this.preferredTimeOptions.some(option => option.value === this.form.get('preferred_time').value);
    }

    ngOnDestroy() {
        if (this.formSub) this.formSub.unsubscribe();
    }

    public get hint(): string {
        if (this.tooManyExtras) return 'One Participants needed for each extras';
        if (this.form.get('first_name').invalid) return 'hints.fill_in_name';
        if (this.form.get('customer_email').invalid) return 'hints.provide_valid_email';
        if (this.form.get('customer_tel').invalid) return 'hints.phone';
        if (this.morePaxNeeded) return this.morePaxNeeded+' more Participants Needed';

        return '';
    }

    public get apiKey(): string {
        return this.http.apiKey;
    }
    public get appKey(): string {
        return this.http.appKey;
    }

    public setForm(): void {
        this.formSettings.forEach(item => {
            const control = this.form.get(item.field);

            if (item.value) control.setValue(item.value);
            if (item.lock) control.disable();
        });
    }

    public book(): void {
        this.bookNow.emit({ form: this.submissionForm, priceOptions: this.session.price_options });
    }

    public enquiry(): void {
        this.enquire.emit({ form: this.submissionForm, priceOptions: this.session.price_options });
    }

    private get submissionForm() {
        return {
            ...this.form.getRawValue(),
            preferred_time: this.hasPreferredTime ? this.form.get('preferred_time').value : null
        };
    }

    public applyAltOnly(option, amount): number{
        if(!+option.alt_only) return amount;

        let allowed_pax = option.alts.reduce((acc,it)=>{
            if(amount < 0){
                if(it.min_pax < option.qty_requested){
                    if(it.min_pax > acc || acc == 0) return it.min_pax;
                    return acc;
                }
                return acc;
            }

            if(amount > 0){
                if(it.min_pax > option.qty_requested){
                    if(it.min_pax < acc || acc == 0) return it.min_pax;
                    return acc;
                }
                return acc;
            }

            return acc;
        },0);
        if(allowed_pax == 0 && amount > 0) return 0;
        amount = allowed_pax - option.qty_requested;
        return amount;
    }

    public changeOption(option: any, amount: number): void {
        if(!+this.package?.independent_pos) return this.changeOptionOld(option, amount);
        let maxPax = +option.max_pax || null;
        let maxPeople = +this.session.max_pax || null;

        amount = this.applyAltOnly(option, amount);

        const ignorePax = false;
        const minPax = +option.min_pax || +this.package.min_pax;
        //const newQty = this.people + amount;
        const newQty = option.qty_requested + amount;
        const newPeople = this.people + amount;

        if (this.session.max_pax && (!maxPax || maxPax > +this.session.max_pax)) maxPax = +this.session.max_pax;

        if (!ignorePax && maxPax && maxPax < newQty) return;
        if (!ignorePax && maxPeople && maxPeople < newPeople) return;

        if (option.qty_requested + amount < 0) return;
        //else if (minPax && newQty < minPax && amount > 0 && !ignorePax) amount = minPax;
        else if (minPax && newQty < minPax && amount > 0 && !ignorePax){
            //this.morePaxNeeded = minPax - amount;
            amount = amount; //increase it as expected
        } 
        //else if (minPax && this.people === minPax && amount < 0 && !ignorePax) amount = -minPax;
        /*else if (minPax && this.people === minPax && amount < 0 && !ignorePax){ 
            amount = amount;
            //this.morePaxNeeded = - (minPax - amount);
        }*/
        else if (maxPax && option.qty_requested === maxPax && amount > 0) return;
        if (option.qty_requested + amount < 0) return;

        option.morePaxNeeded = 0;

        option.qty_requested += amount;
        if(newQty < minPax && newQty != 0){
            option.morePaxNeeded = minPax - newQty;
        }

        this.morePaxNeeded = this.session?.price_options.reduce((acc,it)=>(it?.morePaxNeeded??0)+acc,0);
        this.people = this.session?.price_options.reduce((acc,it)=>(it?.qty_requested??0)+acc,0);

        let extras = this.session?.price_options.filter(it=>+it?.extra).reduce((acc,it)=>(it?.qty_requested??0)+acc,0);

        let morePaxNeeded = 0;

        if(this.people - 2*extras < 0 /*&& !this.morePaxNeeded*/){
            let oldmorePaxNeeded = this.morePaxNeeded;
            morePaxNeeded = 2*extras - this.people;
            if(oldmorePaxNeeded >this.morePaxNeeded){
                morePaxNeeded = oldmorePaxNeeded;
            }
            this.tooManyExtras = morePaxNeeded;
        }else{
            this.tooManyExtras = 0;
        }

        this.setAltsPrices();
        this.calculateTotal();
    }

    public changeOptionOld(option: any, amount: number): void {
        let maxPax = +option.max_pax || null;

        amount = this.applyAltOnly(option, amount);
        
        const ignorePax = false;
        const minPax = +option.min_pax || +this.package.min_pax;
        const newQty = this.people + amount;

        if (this.session.max_pax && (!maxPax || maxPax > +this.session.max_pax)) maxPax = +this.session.max_pax;

        if (!ignorePax && maxPax && maxPax < newQty) return;

        if (option.qty_requested + amount < 0) return;
        //else if (minPax && newQty < minPax && amount > 0 && !ignorePax) amount = minPax;
        else if (minPax && newQty < minPax && amount > 0 && !ignorePax){
            //this.morePaxNeeded = minPax - amount;
            amount = amount; //increase it as expected
        } 
        //else if (minPax && this.people === minPax && amount < 0 && !ignorePax) amount = -minPax;
        /*else if (minPax && this.people === minPax && amount < 0 && !ignorePax){ 
            amount = amount;
            //this.morePaxNeeded = - (minPax - amount);
        }*/
        else if (maxPax && option.qty_requested === maxPax && amount > 0) return;
        if (option.qty_requested + amount < 0) return;



        let morePaxNeeded = 0;

        option.qty_requested += amount;
        if(newQty < minPax && newQty != 0){
            morePaxNeeded = minPax - newQty;
        }        
        
        this.people = this.session?.price_options.reduce((acc,it)=>(it?.qty_requested??0)+acc,0);
        if(this.people == 0 && !morePaxNeeded){
            morePaxNeeded = 1;
        }
        let extras = this.session?.price_options.filter(it=>+it?.extra).reduce((acc,it)=>(it?.qty_requested??0)+acc,0);

        if(this.people - 2*extras < 0 /*&& !this.morePaxNeeded*/){
            let oldmorePaxNeeded = morePaxNeeded;
            morePaxNeeded = 2*extras - this.people;
            if(oldmorePaxNeeded > morePaxNeeded){
                morePaxNeeded = oldmorePaxNeeded;
                this.tooManyExtras = morePaxNeeded;
            }
        }else{
            this.tooManyExtras = 0;
        }


        this.morePaxNeeded = morePaxNeeded;
        this.session?.price_options.forEach((it)=>it.morePaxNeeded = morePaxNeeded);

        this.setAltsPricesOld(this.people);
        this.calculateTotal();
    }

    public changeExtra(option: any, amount: number): void {
        let maxPax = +option.max_pax || null;

        if(option.qty_requested == undefined) option.qty_requested = 0;

        const ignorePax = false;
        const minPax = +option.min_pax || 0;

        if (this.session.max_pax && (!maxPax || maxPax > +this.session.max_pax)) maxPax = +this.session.max_pax;

        if (option.qty_requested + amount < 0) return;
        else if (minPax && this.people === minPax && amount < 0 && !ignorePax) amount = -minPax;
        else if (maxPax && option.qty_requested === maxPax && amount > 0) return;
        if (option.qty_requested + amount < 0) return;

        option.qty_requested += amount;

        this.updateField.emit({ field: 'extras', value: this.extras });
        this.calculateTotal();
    }


    private setAltsPrices(): void {
        this.session.price_options = this.session.price_options.map(option => {
            if (!option.alts || !option.alts.length) return option;

            const price = option.alts.reduce((a, opt) => {
                const val = option.qty_requested || 1;
                return a = +opt.min_pax <= val ? +opt.price : a;
            }, 0);

            if (price <= 0) option.price = option._price;
            else option.price = price;

            return option;
        });
        this.updateField.emit({ field: 'session', value: this.session });
    }

    private setAltsPricesOld(qty: number): void {
        this.session.price_options = this.session.price_options.map(option => {
            if (!option.alts || !option.alts.length) return option;

            const price = option.alts.reduce((a, opt) => {
                const val = qty || 1;
                return a = +opt.min_pax <= val ? +opt.price : a;
            }, 0);

            if (price <= 0) option.price = option._price;
            else option.price = price;

            return option;
        });

        //this.people = qty;

        this.updateField.emit({ field: 'session', value: this.session });
    }


    public calculateExtras(){
        //let extras = this.venue.extras.filter(item=>this.session.extras.find(it=>it == +item.package_id));
        let extras = this.extras;

        let session_ids = this.session?.session_id ? [this.session?.session_id] : [];
        session_ids = [...new Set(session_ids.reduce((acc,val) => acc.concat(val,[]),[]))];

        this.sessionsForExtras = {};

        this.availabilities = [this.session];

        this.extras = extras.filter(it=>/*it.capacity === null || it.capacity==='' ||*/ this.availabilities.filter(item=>{ 
            if(item.session_id != this.session.session_id) return false;
            if(it.package_id==item.package_id || item.package_ids && item.package_ids.find(it2=>it2 == it.package_id)){
                this.sessionsForExtras[it.package_id] = item;
                if(item.max_group === 0 || item.max_pax != '' && item.max_pax !== null && it.qty_requested > item.max_pax){
                    //set it to 0
                    it.qty_requested = 0; it.price_options.map(itpo=>itpo.qty_requested=0);
                }
                if(item.max_pax == 0 || item.max_group == 0) return false;
                return true;
            }
            return false;
        }));
    }

    public askQuestion(e): void {
        this.form.get('message').setValue(e.detail);
    }

    public calculateTotal(){
        const itemTotal = (item: { qty_requested?: number | string; price: number | string;
            price_calc?: number | string; price_group?: number | string; price_group_calc?: number | string }): number => {
            const qty = +(item.qty_requested ?? 0);
            if (qty <= 0) return 0;
            const groupPrice = +(item.price_group_calc ?? item.price_group ?? 0);
            return groupPrice !== 0 ? groupPrice : qty * +(item.price_calc ?? item.price);
        };
        const total = this.session.price_options.reduce((acc, item) => acc + itemTotal(item), 0)
            + this.extras.reduce((acc, item) => acc + itemTotal(item), 0);
        this.total = total;
        this.fee = this.payment.getBookingFee(total);
    }
}
